import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Cell,
} from 'recharts';
import API from '../services/api';
import { parseLocalDate } from '../dateOnly';
import { summarizeTherapist, sortForAttention, STATUS, STATUS_META } from '../therapistPerformance';

// Admin dashboard "Therapist Performance" widget (registry id `trends`).
// One row per active therapist, attention first; click a row for the
// week-by-week chart of Acuity appointments scheduled vs. what the therapist
// collected in the portal (sessions + no-show + cancellation fees).
// Data: GET /admin/therapist-performance (see therapistPerformance.js for the
// status / trend rules, which are unit-tested).

// Series colors validated with the dataviz palette checker (light surface):
// blue / orange / violet clear every check. "Scheduled" is a hollow outline
// so it never competes with the collected stack for a color slot.
const SERIES = {
  sessions: '#2a78d6',
  noShows: '#eb6834',
  cancels: '#4a3aa7',
  scheduledStroke: '#898781',
};
const INK = { muted: '#898781', grid: '#e1e0d9', axis: '#c3c2b7' };

const RANGES = [8, 12, 26];

export default function TherapistPerformanceCard({ profiles = {}, onOpenProfile }) {
  const [weeks, setWeeks] = useState(8);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let active = true;
    setError(null);
    API.get('/admin/therapist-performance', { params: { weeks } })
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setError('Could not load therapist performance.'); });
    return () => { active = false; };
  }, [weeks]);

  const rows = useMemo(() => {
    if (!data) return [];
    return sortForAttention(data.therapists.map((t) => ({ ...t, summary: summarizeTherapist(t) })));
  }, [data]);

  const counts = useMemo(() => {
    const c = { [STATUS.BEHIND]: 0, [STATUS.WATCH]: 0, [STATUS.ON_TRACK]: 0, [STATUS.NO_GOAL]: 0 };
    rows.forEach((r) => { c[r.summary.status] += 1; });
    return c;
  }, [rows]);

  if (error) return <div style={{ color: 'var(--error-color)', fontSize: 13 }}>{error}</div>;
  if (!data) return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading therapist performance…</div>;

  return (
    <div>
      {/* Header strip: status counts + range toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
          {[STATUS.BEHIND, STATUS.WATCH, STATUS.ON_TRACK, STATUS.NO_GOAL].map((s) => (
            <span key={s} style={{ ...chipStyle, background: STATUS_META[s].bg, color: STATUS_META[s].color }}>
              {STATUS_META[s].icon} {counts[s]} {STATUS_META[s].label.toLowerCase()}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 4 }}>Weeks</span>
          {RANGES.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setWeeks(n)}
              style={{
                ...rangeBtn,
                background: n === weeks ? 'var(--primary-color)' : 'var(--white)',
                color: n === weeks ? '#fff' : 'var(--text-strong)',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        Status = last 4 completed weeks of collected sessions vs. each therapist's weekly goal.
        Collected counts everything submitted, including no-show and cancellation fees.
        Calendar data since {fmtDate(data.dataSince)}.
      </div>

      {rows.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No active therapists.</div>
      ) : (
        rows.map((t) => (
          <TherapistRow
            key={t.therapistId}
            t={t}
            profile={profiles[t.therapistId]}
            open={openId === t.therapistId}
            onToggle={() => setOpenId(openId === t.therapistId ? null : t.therapistId)}
            onOpenProfile={onOpenProfile}
          />
        ))
      )}
    </div>
  );
}

function TherapistRow({ t, profile, open, onToggle, onOpenProfile }) {
  const s = t.summary;
  const meta = STATUS_META[s.status];
  const cur = s.current || {};
  const goal = s.goal;
  const pct = goal > 0 ? Math.min(1, (cur.billed || 0) / goal) : 0;

  return (
    <div style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        aria-expanded={open}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(170px, 1.2fr) 118px minmax(180px, 1.4fr) 92px minmax(200px, 1.6fr) 20px',
          gap: 12, alignItems: 'center', padding: '9px 6px', cursor: 'pointer',
          background: open ? 'var(--surface-muted)' : 'transparent',
        }}
      >
        {/* Identity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <Avatar profile={profile} first={t.firstName} last={t.lastName} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t.firstName} {t.lastName}
              {s.bonusLastWeek && <span title="Hit the bonus threshold last week" style={{ marginLeft: 6 }}>⭐</span>}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {profile?.license_type || ''}
              {profile ? (profile.accepting_clients ? ' · accepting' : ' · not accepting') : ''}
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <span style={{ ...chipStyle, background: meta.bg, color: meta.color }}>
            {meta.icon} {meta.label}
          </span>
          <span title={trendTitle(s)} style={{ marginLeft: 6, fontSize: 13, color: 'var(--text-muted)' }}>
            {s.trend === 'up' ? '↑' : s.trend === 'down' ? '↓' : '→'}
          </span>
        </div>

        {/* This week meter */}
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-strong)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ color: 'var(--text-muted)' }}>This week </span>
            <strong>{cur.billed || 0}</strong> collected · {cur.scheduled || 0} booked
            {goal > 0 ? <span style={{ color: 'var(--text-muted)' }}> · goal {goal}</span> : null}
          </div>
          <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'var(--surface-muted)', overflow: 'hidden' }}>
            <div style={{ width: `${pct * 100}%`, height: '100%', background: goal > 0 ? meta.color : 'var(--border-color)', borderRadius: 3 }} />
          </div>
          {t.nextWeek && (
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3, whiteSpace: 'nowrap' }}>
              Next week <strong style={{ color: 'var(--text-strong)' }}>{t.nextWeek.scheduled}</strong> booked
              {goal > 0 && t.nextWeek.scheduled < goal ? <span> · {goal - t.nextWeek.scheduled} short of goal</span> : null}
            </div>
          )}
        </div>

        {/* Sparkline of collected per week */}
        <Sparkline weeks={t.weeks} goal={goal} />

        {/* Signals */}
        <div style={{ fontSize: 12, color: 'var(--text-strong)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span title="No-shows in the last 4 completed weeks (arrow vs. the 4 before)">
            No-shows <strong>{s.noShows4}</strong>
            <span style={{ color: s.noShowTrend === 'up' ? 'var(--error-color)' : 'var(--text-muted)' }}>
              {' '}{s.noShowTrend === 'up' ? '↑' : s.noShowTrend === 'down' ? '↓' : '→'}
            </span>
          </span>
          <span title="Canceled ÷ all Acuity appointments, last 4 completed weeks">
            Cancel <strong>{s.cancelRate == null ? '–' : `${Math.round(s.cancelRate * 100)}%`}</strong>
          </span>
          <span title="Open debt sessions">
            Debt <strong>{t.debtCount}</strong>{t.debtTotal ? <span style={{ color: 'var(--text-muted)' }}> (${Number(t.debtTotal).toFixed(0)})</span> : null}
          </span>
          {s.unbilledFlag && (
            <span title="Kept appointments with no matching submission, last 4 completed weeks" style={{ color: '#B45309', fontWeight: 700 }}>
              ⚠ {s.unbilledGap} unbilled?
            </span>
          )}
        </div>

        <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'right' }}>{open ? '▴' : '▾'}</div>
      </div>

      {open && <WeeklyChart t={t} onOpenProfile={onOpenProfile} />}
    </div>
  );
}

function Avatar({ profile, first, last }) {
  const initials = `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase();
  if (profile?.photo_url) {
    return <img src={profile.photo_url} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--border-color)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

// Tiny bar sparkline: collected per week, current week lighter, goal as a hairline.
function Sparkline({ weeks, goal }) {
  const W = 88; const H = 26; const n = weeks.length;
  const max = Math.max(goal || 0, ...weeks.map((w) => w.billed || 0), 1);
  const slot = W / n; const bw = Math.max(2, Math.min(8, slot - 2));
  const y = (v) => H - (v / max) * (H - 2);
  return (
    <svg width={W} height={H} aria-label="Collected sessions by week" style={{ display: 'block' }}>
      {goal > 0 && <line x1={0} x2={W} y1={y(goal)} y2={y(goal)} stroke={INK.axis} strokeWidth={1} />}
      {weeks.map((w, i) => {
        const h = H - y(w.billed || 0);
        const last = i === n - 1;
        return (
          <rect
            key={w.weekStart}
            x={i * slot + (slot - bw) / 2}
            y={H - h}
            width={bw}
            height={h}
            rx={1.5}
            fill={SERIES.sessions}
            opacity={last ? 0.45 : 1}
          >
            <title>{`Week of ${fmtDate(w.weekStart)}: ${w.billed || 0} collected`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

function WeeklyChart({ t, onOpenProfile }) {
  const s = t.summary;
  const rows = t.weeks.map((w, i) => ({
    ...w,
    label: fmtShort(w.weekStart),
    isCurrent: i === t.weeks.length - 1,
  }));
  // Next week: only the calendar side exists yet, shown as a trailing hollow bar.
  if (t.nextWeek) {
    rows.push({
      weekStart: t.nextWeek.weekStart, scheduled: t.nextWeek.scheduled, canceled: t.nextWeek.canceled,
      billed: 0, sessions: 0, noShows: 0, cancels: 0, collected: 0,
      label: `${fmtShort(t.nextWeek.weekStart)} next`, isNext: true,
    });
  }
  const yMax = Math.max(s.goal, s.bonus, ...rows.map((r) => Math.max(r.scheduled, r.billed)));

  return (
    <div style={{ padding: '6px 6px 14px' }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-strong)', marginBottom: 6 }}>
        <span>Last 4 wks avg <strong>{s.avg4.toFixed(1)}</strong>{s.completedCount >= 8 ? <span style={{ color: 'var(--text-muted)' }}> (prior 4: {s.prevAvg4.toFixed(1)})</span> : null}</span>
        {s.goal > 0 && <span>Goal <strong>{s.goal}</strong>{s.bonus > 0 ? <span style={{ color: 'var(--text-muted)' }}> · bonus at {s.bonus}</span> : null}</span>}
        <span>No-shows last 4 wks <strong>{s.noShows4}</strong>{s.completedCount >= 8 ? <span style={{ color: 'var(--text-muted)' }}> (prior 4: {s.noShowsPrior4})</span> : null}</span>
        {onOpenProfile && (
          <button type="button" onClick={() => onOpenProfile(t.therapistId)} style={linkBtn}>Open profile →</button>
        )}
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barGap={3} barCategoryGap="30%">
          <CartesianGrid vertical={false} stroke={INK.grid} strokeWidth={1} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: INK.muted }} axisLine={{ stroke: INK.axis }} tickLine={false} />
          <YAxis allowDecimals={false} domain={[0, Math.max(4, Math.ceil(yMax * 1.1))]} tick={{ fontSize: 11, fill: INK.muted }} axisLine={false} tickLine={false} />
          <Tooltip content={<WeekTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          {s.goal > 0 && (
            <ReferenceLine y={s.goal} stroke="var(--success-color)" strokeDasharray="4 3" label={{ value: `goal ${s.goal}`, position: 'insideTopRight', fill: INK.muted, fontSize: 10 }} />
          )}
          {s.bonus > 0 && s.bonus !== s.goal && (
            <ReferenceLine y={s.bonus} stroke={INK.axis} strokeDasharray="2 3" label={{ value: `bonus ${s.bonus}`, position: 'insideTopRight', fill: INK.muted, fontSize: 10 }} />
          )}
          <Bar dataKey="scheduled" name="Scheduled (Acuity)" fill="transparent" stroke={SERIES.scheduledStroke} strokeWidth={1.5} barSize={16} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="sessions" name="Sessions" stackId="c" fill={SERIES.sessions} stroke="#fff" strokeWidth={1} barSize={16} isAnimationActive={false}>
            {rows.map((r) => <Cell key={r.weekStart} fillOpacity={r.isCurrent ? 0.5 : 1} />)}
          </Bar>
          <Bar dataKey="noShows" name="No-show fees" stackId="c" fill={SERIES.noShows} stroke="#fff" strokeWidth={1} barSize={16} isAnimationActive={false}>
            {rows.map((r) => <Cell key={r.weekStart} fillOpacity={r.isCurrent ? 0.5 : 1} />)}
          </Bar>
          <Bar dataKey="cancels" name="Cancellation fees" stackId="c" fill={SERIES.cancels} stroke="#fff" strokeWidth={1} barSize={16} radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {rows.map((r) => <Cell key={r.weekStart} fillOpacity={r.isCurrent ? 0.5 : 1} />)}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      {/* Hand-rolled legend: the hollow Scheduled bar has no fill, so the chart
          library's auto legend can't draw a swatch for it. Text stays in ink
          tokens; the swatch carries the series color. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', fontSize: 12, color: 'var(--text-strong)', marginTop: 4 }}>
        <LegendKey label="Scheduled (Acuity, kept)" swatch={{ border: `1.5px solid ${SERIES.scheduledStroke}`, background: 'transparent' }} />
        <LegendKey label="Sessions" swatch={{ background: SERIES.sessions }} />
        <LegendKey label="No-show fees" swatch={{ background: SERIES.noShows }} />
        <LegendKey label="Cancellation fees" swatch={{ background: SERIES.cancels }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
        The filled stack is what was submitted in the portal. The lighter bar is the current, unfinished week; the last hollow bar is what is already booked for next week.
      </div>
    </div>
  );
}

function WeekTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const w = payload[0].payload;
  return (
    <div style={{ background: 'var(--white)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 10px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        Week of {fmtDate(w.weekStart)}{w.isCurrent ? ' (in progress)' : w.isNext ? ' (next week)' : ''}
      </div>
      <Row k={w.isNext ? 'Booked so far' : 'Scheduled (kept)'} v={w.scheduled} sw={null} />
      <Row k="Canceled on calendar" v={w.canceled} sw={null} />
      {w.isNext ? null : <Row k="Collected total" v={w.billed} sw={null} bold />}
      <Row k="Sessions" v={w.sessions} sw={SERIES.sessions} />
      <Row k="No-show fees" v={w.noShows} sw={SERIES.noShows} />
      <Row k="Cancellation fees" v={w.cancels} sw={SERIES.cancels} />
      {w.collected && !w.isNext ? <Row k="$ collected" v={`$${Number(w.collected).toFixed(0)}`} sw={null} /> : null}
    </div>
  );
}
function LegendKey({ label, swatch }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, ...swatch }} />
      {label}
    </span>
  );
}
function Row({ k, v, sw, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, fontWeight: bold ? 700 : 400 }}>
      <span style={{ color: 'var(--text-muted)' }}>
        {sw && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: sw, marginRight: 6 }} />}
        {k}
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );
}

function trendTitle(s) {
  const dir = s.trend === 'up' ? 'up' : s.trend === 'down' ? 'down' : 'flat';
  return `Last 4 weeks avg ${s.avg4.toFixed(1)} vs prior 4 weeks ${s.prevAvg4.toFixed(1)} — ${dir}`;
}
function fmtDate(ymd) {
  const d = parseLocalDate(ymd);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}
function fmtShort(ymd) {
  const d = parseLocalDate(ymd);
  return d ? `${d.getMonth() + 1}/${d.getDate()}` : '';
}

const chipStyle = {
  display: 'inline-block', borderRadius: 999, padding: '2px 8px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
};
const rangeBtn = {
  border: '1px solid var(--border-color)', borderRadius: 6, padding: '3px 9px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const linkBtn = {
  background: 'transparent', border: 'none', color: 'var(--primary-color)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', padding: 0, marginLeft: 'auto',
};
